import { fetchWithRetries } from "./util.ts";
import type { Locale } from "../types";
import type { ProductFetchEvent } from "../types/dom";

export type LimitedCardInfo = {
  variants: {
    checkout1: string;
    checkout2: string;
    medusa: string;
    sales?: string;
  };
  prices: {
    price: number;
    currency: string;
  };
  sold: number;
  apiUrl: string;
};

export type CountriesWithDeliveryEstimationsType = {
  code: string;
  name: string;
  from: number;
  to: number;
  regionId: string;
};

type CountriesWithDeliveryEstimationsDTOType = {
  id: number;
  iso_2: string;
  iso_3: string;
  num_code: number;
  name: string;
  display_name: string;
  region_id: string;
  est_delivery_from: number;
  est_delivery_to: number;
  est_express_delivery_to?: number;
  est_express_delivery_from?: number;
};

export type CountriesByRegionsType = {
  regions: {
    countries: Array<CountriesWithDeliveryEstimationsDTOType>;
  }[];
};

type CoinPriceType = {
  price: number;
  priceChange24h: number;
};

type CoinQuotesType = {
  quotes: Record<string, CoinPriceType>;
};

const TANGEM_STORE_URI = "https://store-api.tangem.com";
const TANGEM_API_URI = "https://api.tangem-tech.com";
// const TANGEM_API_URI = "http://localhost:3000";
export function getResellers(language) {
  const params = language ? `?defaultCode=${language}` : "";
  return fetchWithRetries(`${TANGEM_API_URI}/v1/resellers/${params}`);
}

export async function getGeo() {
  const res = await fetchWithRetries<{ code: string }>(
    `${TANGEM_API_URI}/v1/geo`,
  );
  if (!res || !res.code) {
    const geoUndefinedEvent = new CustomEvent<void>("geo-not-detected");
    document.dispatchEvent(geoUndefinedEvent);
  }
  return { code: res.code };
}

export async function getCountriesWithDeliveryEstimations(): Promise<
  CountriesWithDeliveryEstimationsType[]
> {
  const countriesByRegions = await fetchWithRetries<CountriesByRegionsType>(
    `https://store-api.tangem.com/store/regions`,
  );
  return countriesByRegions.regions
    .flatMap((region) => region.countries)
    .map((country) => {
      const hasExpressDelivery =
        country.est_express_delivery_from && country.est_express_delivery_to;
      return {
        name: country.display_name,
        code: country.iso_2,
        regionId: country.region_id,
        from: hasExpressDelivery
          ? country.est_express_delivery_from!
          : country.est_delivery_from,
        to: hasExpressDelivery
          ? country.est_express_delivery_to!
          : country.est_delivery_to,
      };
    });
}

/**
 * Returns info about products
 * @param {string} language language
 * @return {Promise<Products>} A promise to the Products.
 */
export function getProducts(language) {
  return fetchWithRetries(`${TANGEM_API_URI}/v1/products/${language}`);
}

export function getCoins(limit, offset, timestamp) {
  return fetchWithRetries(
    `${TANGEM_API_URI}/coins/list?limit=${limit}&offset=${offset}&ts=${timestamp}`,
  );
}

export function findCoins(searchText) {
  const TANGEM_API_URI = "https://api.tangem-tech.com";
  return fetchWithRetries(`${TANGEM_API_URI}/coins/find?search=${searchText}`);
}

export function getDiscountCode(code) {
  return fetchWithRetries(`${TANGEM_API_URI}/v1/promocode/${code}`);
}

export async function getNetworks() {
  return await fetchWithRetries(
    `${TANGEM_API_URI}/coins?searchText=bitcoin&acitive=true&page=1&limit=65&networksIds=avalanche,solana,dash,litecoin,xrp,rootstock,kusama,binancecoin,tron,tezos,ethereumfair,polygon-pos,polkadot,fantom,optimistic-ethereum,bitcoin-cash,kava,cronos,aleph-zero,bitcoin,xdai,ravencoin,binance-smart-chain,the-open-network,ethereum-classic,terra,dogecoin,ethereum,stellar,cardano,arbitrum-one,kaspa,cosmos,terra-2,ethereum-pow-iou`,
  );
}

export function sendEmail(body, type = "partner") {
  return fetchWithRetries(`${TANGEM_API_URI}/email/${type}`, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
    },
    redirect: "follow",
    referrerPolicy: "no-referrer",
    body: body,
  });
}

export function dispatchError() {
  const productRequestError = new CustomEvent<ProductFetchEvent>(
    "products_request_fail",
  );
  if (typeof window !== "undefined") {
    document?.dispatchEvent(productRequestError);
  }
}

export async function getProductionRest(sku: string, size = 3) {
  let zeros = "";
  for (let i = 0; i < size; i++) {
    zeros += "0";
  }
  try {
    const data: Record<string, { rest: number }> = await fetchWithRetries(
      `${TANGEM_API_URI}/v1/products/rest/${sku}/`,
    );
    if (!data[sku]) {
      dispatchError();
    }
    return (zeros + data[sku].rest.toString()).slice(-1 * size);
  } catch (e) {
    dispatchError();
    return zeros;
  }
}

const getData = <T>(path: string) =>
  fetchWithRetries<T>(`${import.meta.env.PUBLIC_TANGEM_API_URI}${path}`);

export const getLimitedCardInfo = (
  sku: string,
  { withDraft = true, locale = "en" }: { withDraft?: boolean; locale?: Locale },
): Promise<LimitedCardInfo> =>
  getData<LimitedCardInfo>(
    `v1/products/limited/?sku=${sku}&withDraft=${
      withDraft ? "true" : "false"
    }&locale=${locale}`,
  );

export async function getProductionSold(sku, size = 4, start = 0) {
  let zeros = "";
  for (let i = 0; i < size; i++) {
    zeros += "0";
  }
  try {
    const data: Record<string, { sold: number }> = await fetchWithRetries(
      `${TANGEM_API_URI}/v1/products/sold/${sku}/`,
    );
    if (!data[sku]) {
      dispatchError();
    }
    return (zeros + (start + data[sku].sold).toString()).slice(-1 * size);
  } catch (e) {
    dispatchError();
    return zeros;
  }
}

export async function calculateProductionRest(
  skus: string[],
  size = 4,
  start = 0,
) {
  let zeros = "";
  for (let i = 0; i < size; i++) {
    zeros += "0";
  }
  try {
    const data = await Promise.all(
      skus.map(async (sku: string) =>
        fetchWithRetries<[Record<string, Record<"sold", number>>]>(
          `${TANGEM_API_URI}/v1/products/sold/${sku}/`,
        ),
      ),
    );
    const soldSum = data.reduce((total, item) => {
      const key = Object.keys(item)[0];
      return total + item[key].sold;
    }, 0);
    return (zeros + (start - soldSum).toString()).slice(-1 * size);
  } catch (e) {
    dispatchError();
    return zeros;
  }
}

export const getCoinQuotes = (
  coinId: string[],
  currencyId = "usd",
): Promise<CoinQuotesType> =>
  getData<CoinQuotesType>(
    `v1/quotes/?currencyId=${currencyId}&coinIds=${coinId.join(
      ",",
    )}&fields=price,priceChange24h`,
  );

export function getAvailableRings(): Promise<{
  string: { available: boolean };
}> {
  return fetchWithRetries(`${TANGEM_API_URI}/v1/products/limited/rings/`);
}

function sendClientsDataToStore(
  body: {
    email: string;
    data:
      | { skus: string[] }
      | { page: string; action: string }
      | {
          page: string;
          action: string;
          visaWaitingListData: { name: string; residence: string };
        };
    token: string;
  },
  retries = 3,
) {
  return fetchWithRetries(
    `${TANGEM_STORE_URI}/clients-data`,
    {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
      redirect: "follow",
      referrerPolicy: "no-referrer",
      body: JSON.stringify(body),
    },
    retries,
  );
}

export function sendEmailForRingNotification(
  email: string,
  skus: string[],
  token: string,
) {
  return sendClientsDataToStore({
    email: email,
    data: {
      skus: skus,
    },
    token,
  });
}

export function sendEmailForFollowNotification({
  email,
  token,
  action,
  page = "main",
  retries = 3,
}: {
  email: string;
  token: string;
  action: string;
  page?: string;
  retries?: number;
}) {
  return sendClientsDataToStore(
    {
      email: email,
      data: {
        page,
        action,
      },
      token,
    },
    retries,
  );
}

export function sendDataCardWaitingList({
  email,
  token,
  action,
  page = "cardwaitlist",
  residence,
  name,
}: {
  email: string;
  token: string;
  action: string;
  page?: string;
  name: string;
  residence: string;
}) {
  return sendClientsDataToStore({
    email,
    data: {
      page,
      action,
      visaWaitingListData: { name, residence },
    },
    token,
  });
}
